import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';
import { 
  ArrowLeft, Loader2, BookOpen, ClipboardList, Bell, FileText, Youtube, 
  Download, Upload, CheckCircle, Clock, AlertCircle, Award, ChevronDown,
  ChevronUp, ExternalLink, File, Calendar, Video, MessageCircle, Send, User,
  FileQuestion, Play, Trophy, XCircle, RefreshCcw, Timer, Medal, Crown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentLMS() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');
  const [isLoading, setIsLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [content, setContent] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [expandedContent, setExpandedContent] = useState(null);
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [newQuestion, setNewQuestion] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  // Quiz states
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizTimeLeft, setQuizTimeLeft] = useState(0);
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  // Leaderboard states
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchLMSData();
  }, [navigate]);

  const fetchLMSData = async () => {
    const token = localStorage.getItem('studentToken');
    setIsLoading(true);
    
    try {
      const [dashRes, contentRes, assignRes, announceRes, classesRes, discussRes, quizzesRes] = await Promise.all([
        fetch(`${API_URL}/api/student-portal/lms/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/lms/content`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/lms/assignments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/lms/announcements`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/lms/online-classes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/lms/discussions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/student-portal/lms/quizzes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (dashRes.ok) setDashboard(await dashRes.json());
      if (contentRes.ok) setContent(await contentRes.json());
      if (assignRes.ok) setAssignments(await assignRes.json());
      if (announceRes.ok) setAnnouncements(await announceRes.json());
      if (classesRes.ok) setOnlineClasses(await classesRes.json());
      if (discussRes.ok) setDiscussions(await discussRes.json());
      if (quizzesRes.ok) setQuizzes(await quizzesRes.json());
      
    } catch (error) {
      console.error('Error fetching LMS data:', error);
      toast.error('Failed to load LMS data');
    } finally {
      setIsLoading(false);
    }
  };

  const joinOnlineClass = async (classId) => {
    const token = localStorage.getItem('studentToken');
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/online-classes/${classId}/join`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Attendance marked! Joining class...', { duration: 3000 });
        window.open(data.meeting_link, '_blank');
        fetchLMSData(); // Refresh to show attendance status
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to join class');
      }
    } catch (error) {
      toast.error('Failed to join class');
    }
  };

  const leaveOnlineClass = async (classId) => {
    const token = localStorage.getItem('studentToken');
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/online-classes/${classId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Left class. Duration: ${data.duration_minutes} minutes`);
        fetchLMSData();
      }
    } catch (error) {
      console.error('Leave class error:', error);
    }
  };

  const postQuestion = async () => {
    if (!newQuestion.trim()) return;
    const token = localStorage.getItem('studentToken');
    
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/discussions`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: newQuestion })
      });
      
      if (res.ok) {
        toast.success('Question posted!');
        setNewQuestion('');
        fetchLMSData();
      }
    } catch (error) {
      toast.error('Failed to post question');
    }
  };

  const postReply = async (discussionId) => {
    if (!replyText.trim()) return;
    const token = localStorage.getItem('studentToken');
    
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/discussions`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyText, parent_id: discussionId })
      });
      
      if (res.ok) {
        toast.success('Reply posted!');
        setReplyText('');
        setReplyingTo(null);
        fetchLMSData();
      }
    } catch (error) {
      toast.error('Failed to post reply');
    }
  };

  // Quiz Functions
  const startQuiz = async (quiz) => {
    const token = localStorage.getItem('studentToken');
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/quizzes/${quiz.id}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setActiveQuiz({
          ...quiz,
          attempt_id: data.attempt_id
        });
        setQuizQuestions(data.questions || []);
        setQuizTimeLeft(data.duration_minutes * 60);
        setQuizAnswers({});
        setQuizResult(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to start quiz');
      }
    } catch (error) {
      toast.error('Failed to start quiz');
    }
  };

  const submitQuiz = async () => {
    if (!activeQuiz) return;
    const token = localStorage.getItem('studentToken');
    setSubmittingQuiz(true);
    
    try {
      const answers = quizQuestions.map((q, idx) => ({
        question_index: q.index !== undefined ? q.index : idx,
        selected_option: quizAnswers[idx] !== undefined ? quizAnswers[idx] : -1
      }));
      
      const res = await fetch(`${API_URL}/api/student-portal/lms/quizzes/${activeQuiz.id}/submit`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers })
      });
      
      if (res.ok) {
        const result = await res.json();
        setQuizResult(result);
        toast.success('Quiz submitted!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit quiz');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const exitQuiz = () => {
    setActiveQuiz(null);
    setQuizQuestions([]);
    setQuizAnswers({});
    setQuizResult(null);
    fetchLMSData(); // Refresh to get updated attempt info
  };

  // Timer effect for quiz
  useEffect(() => {
    if (activeQuiz && quizTimeLeft > 0 && !quizResult) {
      const timer = setInterval(() => {
        setQuizTimeLeft(prev => {
          if (prev <= 1) {
            // Auto submit when time is up
            submitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [activeQuiz, quizResult]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Leaderboard function
  const fetchLeaderboard = async (quizId) => {
    const token = localStorage.getItem('studentToken');
    setLoadingLeaderboard(true);
    try {
      const res = await fetch(`${API_URL}/api/student-portal/lms/quizzes/${quizId}/leaderboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeaderboardData(data);
        setShowLeaderboard(true);
      } else {
        toast.error('Failed to load leaderboard');
      }
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  // Download Certificate function
  const downloadCertificate = async (quizId) => {
    const token = localStorage.getItem('studentToken');
    try {
      toast.loading('Generating certificate...', { id: 'cert-download' });
      const res = await fetch(`${API_URL}/api/student-portal/lms/quizzes/${quizId}/certificate`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Get filename from Content-Disposition header or use default
        const contentDisposition = res.headers.get('Content-Disposition');
        let filename = 'quiz_certificate.pdf';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1].replace(/['"]/g, '');
        }
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Certificate downloaded successfully!', { id: 'cert-download' });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to download certificate', { id: 'cert-download' });
      }
    } catch (error) {
      console.error('Certificate download error:', error);
      toast.error('Failed to download certificate', { id: 'cert-download' });
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="text-yellow-500" size={20} />;
    if (rank === 2) return <Medal className="text-gray-400" size={20} />;
    if (rank === 3) return <Medal className="text-amber-600" size={20} />;
    return <span className="font-bold text-gray-500">#{rank}</span>;
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : null;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSubmitAssignment = async (assignmentId) => {
    const token = localStorage.getItem('studentToken');
    setUploading(true);

    try {
      let fileUrl = null;
      
      // Upload file if selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        
        const uploadRes = await fetch(`${API_URL}/api/student-portal/lms/submissions/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (!uploadRes.ok) {
          const error = await uploadRes.json();
          throw new Error(error.detail || 'File upload failed');
        }
        
        const uploadData = await uploadRes.json();
        fileUrl = uploadData.file_url;
      }
      
      // Submit assignment
      const submitRes = await fetch(`${API_URL}/api/student-portal/lms/assignments/${assignmentId}/submit`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!submitRes.ok) {
        const error = await submitRes.json();
        throw new Error(error.detail || 'Submission failed');
      }
      
      const submitData = await submitRes.json();
      
      // Attach file if uploaded
      if (fileUrl) {
        await fetch(`${API_URL}/api/student-portal/lms/submissions/${submitData.id}/file?file_url=${encodeURIComponent(fileUrl)}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      toast.success('Assignment submitted successfully!');
      setSelectedFile(null);
      setExpandedAssignment(null);
      fetchLMSData();
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.message || 'Failed to submit assignment');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full"><Clock size={12} /> Submitted</span>;
      case 'graded':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full"><CheckCircle size={12} /> Graded</span>;
      case 'pending':
      default:
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"><AlertCircle size={12} /> Pending</span>;
    }
  };

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-l-4 border-red-500 bg-red-50';
      case 'important': return 'border-l-4 border-yellow-500 bg-yellow-50';
      default: return 'border-l-4 border-blue-500 bg-blue-50';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">LMS - Learning</h1>
              <p className="text-sm text-gray-500">{dashboard?.course_name || 'Course Content'}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Total Content</p>
                  <p className="text-2xl font-bold">{dashboard?.total_content || 0}</p>
                </div>
                <BookOpen className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Assignments</p>
                  <p className="text-2xl font-bold">{dashboard?.total_assignments || 0}</p>
                </div>
                <ClipboardList className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Pending</p>
                  <p className="text-2xl font-bold">{dashboard?.pending_assignments || 0}</p>
                </div>
                <Clock className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Announcements</p>
                  <p className="text-2xl font-bold">{dashboard?.new_announcements || 0}</p>
                </div>
                <Bell className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'content', label: 'Content', icon: BookOpen, count: content.length },
            { id: 'assignments', label: 'Assignments', icon: ClipboardList, count: assignments.length },
            { id: 'quizzes', label: 'Quizzes', icon: FileQuestion, count: quizzes.length },
            { id: 'classes', label: 'Live Classes', icon: Video, count: onlineClasses.length },
            { id: 'discussions', label: 'Ask Question', icon: MessageCircle, count: discussions.length },
            { id: 'announcements', label: 'Notices', icon: Bell, count: announcements.length }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap ${activeTab === tab.id ? 'bg-indigo-600' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label} ({tab.count})
            </Button>
          ))}
        </div>

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-4">
            {content.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BookOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No course content available yet</p>
                </CardContent>
              </Card>
            ) : (
              content.map((item) => (
                <Card key={item.id} className="overflow-hidden" data-testid={`content-${item.id}`}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedContent(expandedContent === item.id ? null : item.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${item.content_type === 'youtube' ? 'bg-red-100' : 'bg-blue-100'}`}>
                          {item.content_type === 'youtube' ? (
                            <Youtube className="w-5 h-5 text-red-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-base">{item.title}</CardTitle>
                          <CardDescription>{item.content_type?.toUpperCase()} • {item.views || 0} views</CardDescription>
                        </div>
                      </div>
                      {expandedContent === item.id ? <ChevronUp /> : <ChevronDown />}
                    </div>
                  </CardHeader>
                  
                  {expandedContent === item.id && (
                    <CardContent className="border-t bg-gray-50">
                      {item.description && (
                        <p className="text-gray-600 mb-4">{item.description}</p>
                      )}
                      
                      {item.content_type === 'youtube' && getYoutubeEmbedUrl(item.content_url) && (
                        <div className="aspect-video mb-4">
                          <iframe
                            src={getYoutubeEmbedUrl(item.content_url)}
                            title={item.title}
                            className="w-full h-full rounded-lg"
                            allowFullScreen
                          />
                        </div>
                      )}
                      
                      {item.content_url && item.content_type !== 'youtube' && (
                        <Button 
                          onClick={() => window.open(item.content_url, '_blank')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download / View
                        </Button>
                      )}
                      
                      {item.content_type === 'youtube' && item.content_url && (
                        <Button 
                          variant="outline"
                          onClick={() => window.open(item.content_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Open in YouTube
                        </Button>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))
            )}
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="space-y-4">
            {assignments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No assignments available yet</p>
                </CardContent>
              </Card>
            ) : (
              assignments.map((assignment) => {
                const isPastDue = new Date(assignment.due_date) < new Date();
                const canSubmit = !isPastDue || assignment.allow_late_submission;
                
                return (
                  <Card key={assignment.id} className={`overflow-hidden ${isPastDue && assignment.submission_status === 'pending' ? 'border-red-200' : ''}`} data-testid={`assignment-${assignment.id}`}>
                    <CardHeader 
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedAssignment(expandedAssignment === assignment.id ? null : assignment.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${assignment.submission_status === 'graded' ? 'bg-green-100' : assignment.submission_status === 'submitted' ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                            <ClipboardList className={`w-5 h-5 ${assignment.submission_status === 'graded' ? 'text-green-600' : assignment.submission_status === 'submitted' ? 'text-yellow-600' : 'text-gray-600'}`} />
                          </div>
                          <div>
                            <CardTitle className="text-base">{assignment.title}</CardTitle>
                            <CardDescription className="flex items-center gap-2">
                              <Calendar className="w-3 h-3" />
                              Due: {formatDate(assignment.due_date)} • {assignment.total_marks} marks
                            </CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(assignment.submission_status)}
                          {expandedAssignment === assignment.id ? <ChevronUp /> : <ChevronDown />}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedAssignment === assignment.id && (
                      <CardContent className="border-t bg-gray-50">
                        <div className="space-y-4">
                          <p className="text-gray-600">{assignment.description}</p>
                          
                          {/* Show marks if graded */}
                          {assignment.submission_status === 'graded' && (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Award className="w-5 h-5 text-green-600" />
                                <span className="font-semibold text-green-800">Result</span>
                              </div>
                              <p className="text-2xl font-bold text-green-700">
                                {assignment.marks_obtained} / {assignment.total_marks}
                              </p>
                            </div>
                          )}
                          
                          {/* Show submission info */}
                          {assignment.submitted_at && (
                            <p className="text-sm text-gray-500">
                              Submitted on: {new Date(assignment.submitted_at).toLocaleString('en-IN')}
                            </p>
                          )}
                          
                          {/* Submit form - only if can submit */}
                          {assignment.submission_status === 'pending' && canSubmit && (
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <h4 className="font-semibold text-blue-800 mb-3">Submit Assignment</h4>
                              
                              <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Upload File (PDF, DOC, ZIP - Max 10MB)
                                </label>
                                <input
                                  type="file"
                                  onChange={handleFileSelect}
                                  accept=".pdf,.doc,.docx,.zip,.rar,.txt,.jpg,.jpeg,.png"
                                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                                {selectedFile && (
                                  <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                    <File className="w-4 h-4" />
                                    {selectedFile.name}
                                  </p>
                                )}
                              </div>
                              
                              <Button 
                                onClick={() => handleSubmitAssignment(assignment.id)}
                                disabled={uploading}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {uploading ? (
                                  <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Submitting...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4 mr-2" />
                                    Submit Assignment
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                          
                          {/* Past due warning */}
                          {isPastDue && assignment.submission_status === 'pending' && !assignment.allow_late_submission && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                              <AlertCircle className="w-4 h-4 inline mr-2" />
                              Deadline has passed. Late submissions are not allowed.
                            </div>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <div className="space-y-4">
            {announcements.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bell className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No announcements yet</p>
                </CardContent>
              </Card>
            ) : (
              announcements.map((announcement) => (
                <Card key={announcement.id} className={getPriorityStyle(announcement.priority)} data-testid={`announcement-${announcement.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {announcement.priority === 'urgent' && <AlertCircle className="w-4 h-4 text-red-600" />}
                          <CardTitle className="text-base">{announcement.title}</CardTitle>
                          {announcement.priority === 'urgent' && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">Urgent</span>
                          )}
                          {announcement.priority === 'important' && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">Important</span>
                          )}
                        </div>
                        <CardDescription>
                          Posted by {announcement.created_by_name} • {formatDate(announcement.created_at)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Quizzes Tab */}
        {activeTab === 'quizzes' && !activeQuiz && (
          <div className="space-y-4">
            {quizzes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileQuestion className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No quizzes available yet</p>
                </CardContent>
              </Card>
            ) : (
              quizzes.map((quiz) => {
                const hasAttempted = quiz.my_attempts > 0;
                const canRetake = quiz.my_attempts < quiz.max_attempts;
                const isPassed = quiz.best_score !== null && quiz.best_score >= quiz.pass_percentage;
                
                return (
                  <Card key={quiz.id} className={`overflow-hidden ${isPassed ? 'border-green-300' : ''}`} data-testid={`quiz-${quiz.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-lg ${isPassed ? 'bg-green-100' : hasAttempted ? 'bg-yellow-100' : 'bg-indigo-100'}`}>
                            {isPassed ? (
                              <Trophy className="text-green-600" size={24} />
                            ) : (
                              <FileQuestion className={hasAttempted ? 'text-yellow-600' : 'text-indigo-600'} size={24} />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-800">{quiz.title}</h3>
                            <p className="text-sm text-gray-500">{quiz.description}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <FileQuestion size={12} />
                                {quiz.total_questions} questions
                              </span>
                              <span className="flex items-center gap-1">
                                <Award size={12} />
                                {quiz.total_marks} marks
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {quiz.duration_minutes} min
                              </span>
                              <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                Pass: {quiz.pass_percentage}%
                              </span>
                            </div>
                            {/* Attempt info */}
                            {hasAttempted && (
                              <div className="mt-2 flex items-center gap-3">
                                <span className={`text-sm font-medium ${isPassed ? 'text-green-600' : 'text-yellow-600'}`}>
                                  Best Score: {quiz.best_score}%
                                </span>
                                <span className="text-xs text-gray-500">
                                  Attempts: {quiz.my_attempts}/{quiz.max_attempts}
                                </span>
                                {isPassed && (
                                  <span className="flex items-center gap-1 text-green-600 text-xs">
                                    <CheckCircle size={12} /> Passed
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {quiz.in_progress ? (
                            <Button 
                              onClick={() => startQuiz(quiz)}
                              className="bg-yellow-600 hover:bg-yellow-700"
                            >
                              <RefreshCcw size={16} className="mr-2" />
                              Continue Quiz
                            </Button>
                          ) : canRetake ? (
                            <Button 
                              onClick={() => startQuiz(quiz)}
                              className={hasAttempted ? 'bg-blue-600 hover:bg-blue-700' : 'bg-indigo-600 hover:bg-indigo-700'}
                            >
                              <Play size={16} className="mr-2" />
                              {hasAttempted ? 'Retake Quiz' : 'Start Quiz'}
                            </Button>
                          ) : (
                            <span className="text-sm text-gray-500">Max attempts reached</span>
                          )}
                          {/* Leaderboard button */}
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => fetchLeaderboard(quiz.id)}
                            disabled={loadingLeaderboard}
                          >
                            <Trophy size={14} className="mr-1" />
                            Leaderboard
                          </Button>
                          {/* Download Certificate button - only for passed quizzes */}
                          {isPassed && (
                            <Button 
                              variant="outline"
                              size="sm"
                              className="text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => downloadCertificate(quiz.id)}
                              data-testid={`download-certificate-${quiz.id}`}
                            >
                              <Download size={14} className="mr-1" />
                              Certificate
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Active Quiz Interface */}
        {activeTab === 'quizzes' && activeQuiz && !quizResult && (
          <div className="space-y-4">
            {/* Quiz Header */}
            <Card className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{activeQuiz.title}</h2>
                    <p className="text-sm opacity-90">{quizQuestions.length} Questions • {activeQuiz.total_marks} Marks</p>
                  </div>
                  <div className={`flex items-center gap-2 text-2xl font-bold ${quizTimeLeft < 60 ? 'text-red-300 animate-pulse' : ''}`}>
                    <Timer size={24} />
                    {formatTime(quizTimeLeft)}
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-3 h-2 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white transition-all duration-300"
                    style={{ width: `${(Object.keys(quizAnswers).length / quizQuestions.length) * 100}%` }}
                  />
                </div>
                <p className="text-xs mt-1 opacity-75">
                  {Object.keys(quizAnswers).length}/{quizQuestions.length} answered
                </p>
              </CardContent>
            </Card>

            {/* Questions */}
            <div className="space-y-4">
              {quizQuestions.map((q, idx) => (
                <Card key={idx} className={quizAnswers[idx] !== undefined ? 'border-green-300' : ''}>
                  <CardContent className="p-4">
                    <div className="mb-3">
                      <span className="text-xs text-indigo-600 font-medium">Question {idx + 1}</span>
                      <span className="text-xs text-gray-500 ml-2">({q.marks} mark{q.marks > 1 ? 's' : ''})</span>
                    </div>
                    <p className="text-gray-800 font-medium mb-4">{q.question}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {q.options.map((option, optIdx) => (
                        <button
                          key={optIdx}
                          onClick={() => setQuizAnswers({...quizAnswers, [idx]: optIdx})}
                          className={`text-left p-3 rounded-lg border-2 transition-all ${
                            quizAnswers[idx] === optIdx 
                              ? 'border-indigo-500 bg-indigo-50' 
                              : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                              quizAnswers[idx] === optIdx 
                                ? 'bg-indigo-600 text-white' 
                                : 'bg-gray-200 text-gray-700'
                            }`}>
                              {String.fromCharCode(65 + optIdx)}
                            </span>
                            <span className={quizAnswers[idx] === optIdx ? 'font-medium' : ''}>{option}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Submit Button */}
            <div className="flex justify-between items-center sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border">
              <Button variant="outline" onClick={exitQuiz}>
                Cancel Quiz
              </Button>
              <Button 
                onClick={submitQuiz}
                disabled={submittingQuiz}
                className="bg-green-600 hover:bg-green-700"
              >
                {submittingQuiz ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Submit Quiz ({Object.keys(quizAnswers).length}/{quizQuestions.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Quiz Result */}
        {activeTab === 'quizzes' && quizResult && (
          <div className="space-y-4">
            <Card className={`border-2 ${quizResult.passed ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'}`}>
              <CardContent className="p-6 text-center">
                {quizResult.passed ? (
                  <Trophy className="w-16 h-16 mx-auto text-green-600 mb-4" />
                ) : (
                  <XCircle className="w-16 h-16 mx-auto text-red-600 mb-4" />
                )}
                <h2 className={`text-2xl font-bold ${quizResult.passed ? 'text-green-700' : 'text-red-700'}`}>
                  {quizResult.passed ? 'Congratulations! You Passed!' : 'Quiz Completed'}
                </h2>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-indigo-600">{quizResult.score}</p>
                    <p className="text-sm text-gray-500">Score</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-indigo-600">{quizResult.total_marks}</p>
                    <p className="text-sm text-gray-500">Total Marks</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className={`text-3xl font-bold ${quizResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                      {quizResult.percentage}%
                    </p>
                    <p className="text-sm text-gray-500">Percentage</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-3xl font-bold text-green-600">{quizResult.correct_count}</p>
                    <p className="text-sm text-gray-500">Correct</p>
                  </div>
                </div>
                <p className="mt-4 text-gray-600">
                  Pass Percentage: {quizResult.pass_percentage}%
                </p>
                {/* Download Certificate button for passed quiz */}
                {quizResult.passed && (
                  <Button 
                    onClick={() => downloadCertificate(quizResult.quiz_id)}
                    className="mt-4 bg-green-600 hover:bg-green-700"
                    data-testid="download-result-certificate"
                  >
                    <Download size={16} className="mr-2" />
                    Download Certificate
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Show Results if available */}
            {quizResult.results && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Answer Review</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {quizResult.results.map((r, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${r.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {r.is_correct ? (
                            <CheckCircle size={16} className="text-green-600" />
                          ) : (
                            <XCircle size={16} className="text-red-600" />
                          )}
                          <span className="font-medium text-sm">Question {r.question_index + 1}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${r.is_correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {r.marks_obtained} / {quizQuestions[r.question_index]?.marks || 1}
                          </span>
                        </div>
                        {!r.is_correct && r.correct_option !== null && (
                          <p className="text-sm text-gray-600">
                            Correct answer: Option {String.fromCharCode(65 + r.correct_option)}
                          </p>
                        )}
                        {r.explanation && (
                          <p className="text-sm text-gray-500 mt-1 italic">{r.explanation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Button onClick={exitQuiz} className="w-full bg-indigo-600 hover:bg-indigo-700">
              Back to Quiz List
            </Button>
          </div>
        )}

        {/* Live Classes Tab */}
        {activeTab === 'classes' && (
          <div className="space-y-4">
            {onlineClasses.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Video className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No upcoming live classes</p>
                </CardContent>
              </Card>
            ) : (
              onlineClasses.map((cls) => (
                <Card key={cls.id} className={`${cls.status === 'live' ? 'border-2 border-red-400 bg-red-50' : ''}`} data-testid={`class-${cls.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-lg ${cls.status === 'live' ? 'bg-red-100 animate-pulse' : 'bg-blue-100'}`}>
                          <Video className={cls.status === 'live' ? 'text-red-600' : 'text-blue-600'} size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-800">{cls.title}</h3>
                            {cls.status === 'live' && (
                              <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full animate-pulse flex items-center gap-1">
                                <span className="w-2 h-2 bg-red-500 rounded-full"></span> LIVE NOW
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{cls.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {formatDate(cls.scheduled_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {cls.scheduled_time} ({cls.duration_minutes} min)
                            </span>
                            <span>{cls.meeting_platform === 'zoom' ? '📹 Zoom' : '🎥 Google Meet'}</span>
                          </div>
                        </div>
                      </div>
                      <Button 
                        onClick={() => joinOnlineClass(cls.id)}
                        className={cls.status === 'live' ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-blue-600 hover:bg-blue-700'}
                      >
                        <ExternalLink size={16} className="mr-2" />
                        {cls.status === 'live' ? 'Join Now' : 'Open Link'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Discussions Tab */}
        {activeTab === 'discussions' && (
          <div className="space-y-4">
            {/* Ask Question Form */}
            <Card className="border-2 border-indigo-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="text-indigo-600" size={20} />
                  Ask a Question to Teacher
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newQuestion}
                    onChange={(e) => setNewQuestion(e.target.value)}
                    placeholder="Type your question here..."
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <Button 
                    onClick={postQuestion}
                    disabled={!newQuestion.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Send size={18} className="mr-1" />
                    Ask
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Discussions List */}
            {discussions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No discussions yet. Ask the first question!</p>
                </CardContent>
              </Card>
            ) : (
              discussions.map((disc) => (
                <Card key={disc.id} className={disc.is_answered ? 'border-green-300 bg-green-50/30' : ''} data-testid={`discussion-${disc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${disc.user_role === 'Student' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                        <User className={disc.user_role === 'Student' ? 'text-blue-600' : 'text-purple-600'} size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800">{disc.user_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${disc.user_role === 'Student' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {disc.user_role}
                          </span>
                          {disc.is_answered && (
                            <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-1">
                              <CheckCircle size={12} /> Answered
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700">{disc.message}</p>
                        
                        {/* Reply button */}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setReplyingTo(replyingTo === disc.id ? null : disc.id)}
                          className="text-gray-500 hover:text-indigo-600 mt-2"
                        >
                          <MessageCircle size={14} className="mr-1" />
                          Reply ({disc.replies?.length || 0})
                        </Button>
                        
                        {/* Replies */}
                        {disc.replies && disc.replies.length > 0 && (
                          <div className="mt-3 space-y-2 border-l-2 border-gray-200 pl-4">
                            {disc.replies.map((reply) => (
                              <div key={reply.id} className="flex gap-2">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${reply.user_role === 'Student' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                                  <User className={reply.user_role === 'Student' ? 'text-blue-600' : 'text-purple-600'} size={14} />
                                </div>
                                <div>
                                  <span className="font-medium text-sm">{reply.user_name}</span>
                                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${reply.user_role === 'Student' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                    {reply.user_role}
                                  </span>
                                  <p className="text-sm text-gray-600">{reply.message}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Reply Form */}
                        {replyingTo === disc.id && (
                          <div className="mt-3 flex gap-2">
                            <input
                              type="text"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Write reply..."
                              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <Button 
                              onClick={() => postReply(disc.id)}
                              disabled={!replyText.trim()}
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700"
                            >
                              <Send size={14} />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Leaderboard Modal */}
        {showLeaderboard && leaderboardData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[80vh] overflow-y-auto">
              <CardHeader className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy size={28} />
                    <div>
                      <CardTitle>Leaderboard</CardTitle>
                      <CardDescription className="text-yellow-100">{leaderboardData.quiz_title}</CardDescription>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowLeaderboard(false)}
                    className="text-white hover:bg-white/20"
                  >
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {leaderboardData.leaderboard.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p>No one has completed this quiz yet.</p>
                    <p className="text-sm">Be the first!</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {leaderboardData.leaderboard.map((entry, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center p-4 ${entry.is_me ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''} ${entry.rank <= 3 ? 'bg-gradient-to-r from-yellow-50/50' : ''}`}
                      >
                        <div className="w-12 flex justify-center">
                          {getRankIcon(entry.rank)}
                        </div>
                        <div className="flex-1 ml-3">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${entry.is_me ? 'text-indigo-700' : 'text-gray-800'}`}>
                              {entry.user_name}
                            </span>
                            {entry.is_me && (
                              <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded">You</span>
                            )}
                            {entry.passed && (
                              <CheckCircle size={14} className="text-green-600" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{entry.attempts} attempt(s)</p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-lg ${entry.rank === 1 ? 'text-yellow-600' : entry.passed ? 'text-green-600' : 'text-gray-700'}`}>
                            {entry.percentage}%
                          </p>
                          <p className="text-xs text-gray-500">{entry.score}/{leaderboardData.total_marks}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {leaderboardData.my_rank && leaderboardData.my_rank > 10 && (
                  <div className="p-4 bg-indigo-50 border-t text-center">
                    <p className="text-indigo-700 font-medium">Your Rank: #{leaderboardData.my_rank}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
